import { useRef, useState, useCallback, useMemo } from 'react'
import styles from './LiveWorksheets.module.scss'
import { useApi } from '../../../api/api'
import GoogleSheets from '../../../assets/canvas/GoogleSheets.png'
import { EVENTS, trackEvent } from '../../../events'
import en from 'javascript-time-ago/locale/en'
import TimeAgo from 'javascript-time-ago'
import {
    DotsThreeIcon,
    PlusIcon,
    TrashIcon,
    ArrowCounterClockwiseIcon,
    UploadSimpleIcon,
    ArrowSquareOutIcon,
    MicrosoftExcelLogoIcon,
    TableIcon,
} from '@phosphor-icons/react'
import { useSelector } from 'react-redux'
import { debounce } from 'lodash'
import UploadWorksheet from '../../Objects/UploadWorksheet/UploadWorksheet'
import { Server } from '../../../api/Server'
import { useModal, MODALS } from '../../modals'
import { useWorkspaceMetadata } from '../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { useWorksheets } from '../../../api/hooks/worksheets/useWorksheets'
import { EmptyDataset } from '../../EmptyDataset/EmptyDataset'
import { ROUTES } from '../../../router/routes'
import { useNavigate } from 'react-router-dom'
import {
    ScoopMenu,
    ScoopMenuItem,
    ScoopScreenLayout,
    ScoopText,
    ScoopButton,
    ScoopTable,
    ScoopSpinner,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
} from '../../ui'
import { HelpBotPromptButton } from '../../HelpBot/components/HelpBotPromptButton/HelpBotPromptButton'
import { HELP_BOT_PROMPTS } from '../../HelpBot/helpBotPrompts'

TimeAgo.addDefaultLocale(en)
const timeAgo = new TimeAgo('en-US')

const NameCellRenderer = ({ data }) => {
    const parsedValue = data.name.split('_')
    const label = parsedValue[0]
    const description = parsedValue[1]
    return (
        <ScoopColumn className={styles.worksheetCell}>
            <ScoopText className={styles.cellLabel}>{label}</ScoopText>
            <ScoopText className={styles.cellDescription}>{description}</ScoopText>
        </ScoopColumn>
    )
}

const PlatformCellRenderer = ({ data }) => {
    return (
        <ScoopColumn className={styles.worksheetCell}>
            <ScoopRow className={styles.platformPill}>
                {data.platform === 'Google Sheets' && <img src={GoogleSheets} alt={'sheets'} />}
                <span>{data.platform}</span>
            </ScoopRow>
        </ScoopColumn>
    )
}

const CreatedByCellRenderer = ({ data }) => {
    return (
        <ScoopColumn className={styles.worksheetCell}>
            <ScoopText className={styles.cellText}>{data.createdBy}</ScoopText>
        </ScoopColumn>
    )
}

const LastEditedCellRenderer = ({ data }) => {
    return (
        <ScoopColumn className={styles.worksheetCell}>
            <ScoopText className={styles.cellTextMuted}>
                {data.lastEdited ? timeAgo.format(data.lastEdited) : 'No date available'}
            </ScoopText>
        </ScoopColumn>
    )
}

const MenuCellRenderer = ({ data, onDelete }) => {
    const [open, setOpen] = useState(false)
    const anchor = useRef()
    const [refreshLoading, setRefreshLoading] = useState(false)

    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const token = useSelector((state) => state.auth.token)

    const refreshInputQuery = async () => {
        const server = new Server(workspaceID, userID, token)
        setRefreshLoading(true)
        try {
            await server.sheetPostData(
                {
                    action: 'addOn',
                    addOnAction: 'refreshInputQuery',
                    sheetName: data.name,
                    sheetID: data.worksheetData.worksheetID,
                },
                () => {}
            )
        } catch (error) {
            console.log('refreshInputQuery error', error)
        } finally {
            setRefreshLoading(false)
        }
    }

    return (
        <ScoopColumn className={styles.worksheetCell}>
            <button onClick={() => setOpen(true)} className={styles.menuButton} ref={anchor}>
                {refreshLoading ? (
                    <ScoopSpinner size={28} />
                ) : (
                    <DotsThreeIcon size={20} weight="bold" />
                )}
            </button>
            <ScoopMenu open={open} anchorEl={anchor.current} onClose={() => setOpen(false)}>
                {data?.worksheetData?.url && (
                    <ScoopMenuItem
                        leftSection={<ArrowSquareOutIcon size={18} />}
                        onClick={() => {
                            window.open(data.worksheetData.url, '_blank').focus()
                            setOpen(false)
                        }}
                    >
                        <ScoopText className={styles.menuItemText}>Open in Google Sheets</ScoopText>
                    </ScoopMenuItem>
                )}
                <ScoopMenuItem
                    leftSection={<ArrowCounterClockwiseIcon size={18} />}
                    onClick={() => {
                        refreshInputQuery()
                        setOpen(false)
                    }}
                >
                    <ScoopText className={styles.menuItemText}>Refresh Worksheet</ScoopText>
                </ScoopMenuItem>
                <ScoopMenuItem
                    leftSection={<TrashIcon size={18} />}
                    onClick={() => {
                        onDelete(data.worksheetData.worksheetID)
                        setOpen(false)
                    }}
                >
                    <ScoopText className={styles.menuItemTextError}>
                        Delete Live Worksheet
                    </ScoopText>
                </ScoopMenuItem>
            </ScoopMenu>
        </ScoopColumn>
    )
}

export const LiveWorksheets = () => {
    const accountID = useSelector((state) => state.auth.accountID)

    const userEmail = useSelector((state) => state.auth.userEmail)
    const { postData } = useApi(
        'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/worksheetactions'
    )
    const navigate = useNavigate()
    const [search, setSearch] = useState('')
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const createWorksheetModal = useModal(MODALS.LIVE_WORKSHEETS.CREATE)

    const handleDeleteClick = useCallback(
        async (worksheetId) => {
            const confirmed = await deleteModal.show({
                title: 'Live Worksheet',
                description: 'Do you want to delete this Worksheet entirely?',
            })
            if (confirmed) {
                await postData({
                    action: 'deleteDynamicSheet',
                    worksheetID: worksheetId,
                    isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
                })
                getSheets()
            }
        },
        [deleteModal, postData]
    )

    const colDefs = useMemo(
        () => [
            {
                field: 'name',
                header: 'Name',
                flex: 2,
                sortable: false,
                render: ({ row }) => <NameCellRenderer data={row} />,
            },
            {
                field: 'platform',
                header: 'Platform',
                flex: 1,
                sortable: false,
                render: ({ row }) => <PlatformCellRenderer data={row} />,
            },
            {
                field: 'createdBy',
                header: 'Created By',
                flex: 1,
                sortable: false,
                render: ({ row }) => <CreatedByCellRenderer data={row} />,
            },
            {
                field: 'lastEdited',
                header: 'Last Edited',
                flex: 1,
                sortable: false,
                render: ({ row }) => <LastEditedCellRenderer data={row} />,
            },
            {
                field: '',
                header: '',
                width: 50,
                sortable: false,
                render: ({ row }) => <MenuCellRenderer data={row} onDelete={handleDeleteClick} />,
            },
        ],
        [handleDeleteClick]
    )

    const [anchorEl, setAnchorEl] = useState(null)
    const [showUploadWorksheet, setShowUploadWorksheet] = useState(false)
    const tourAlertModal = useModal(MODALS.COMMON.TOUR_ALERT)

    const { worksheets: rowData, isLoading, refetch: getSheets } = useWorksheets()
    const { workspaceMetadata: metadata, isLoading: isMetadataLoading } = useWorkspaceMetadata()

    const [filteredData, setFilteredData] = useState(null)

    const debounceFn = useMemo(
        () =>
            debounce((value) => {
                if (!value) {
                    setFilteredData(null)
                } else {
                    setFilteredData(
                        rowData.filter((row) =>
                            row.name.toLowerCase().includes(value.toLowerCase())
                        )
                    )
                }
            }, 1000),
        [rowData]
    )

    const handleRefresh = () => {
        setShowUploadWorksheet(false) // This will close the dialog
        getSheets() // Refresh the sheets immediately after closing
    }

    const handleSearchChange = (value) => {
        setSearch(value)
        debounceFn(value)
    }

    const createWorksheetFromUpload = async () => {
        setAnchorEl(null)
        setShowUploadWorksheet(true)
    }

    const closeUploadWorksheet = () => {
        setShowUploadWorksheet(false)
    }

    const handleOpenCreateWorksheet = async () => {
        setAnchorEl(null)
        const result = await createWorksheetModal.show({
            defaultLinkedAccount: userEmail,
        })
        if (result) {
            await postData({
                action: 'createCalculatedSheet',
                label: result.name,
                googleAccountEmail: result.linkedAccount,
                userName: result.linkedAccount,
                derivedFrom: [],
                changesReports: [],
                description: result.description,
                sortID: '1',
                isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
            })
            trackEvent(EVENTS.LIVE_WORKSHEET.CREATE)
            getSheets()
        }
    }

    const renderOptionDialog = () => {
        return (
            <ScoopMenu
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                className={styles.optionsMenu}
            >
                <ScoopMenuItem
                    leftSection={<PlusIcon size={20} className={styles.primaryIcon} />}
                    primary="New Worksheet"
                    secondary="Create new worksheet from scratch"
                    onClick={handleOpenCreateWorksheet}
                />
                <ScoopMenuItem
                    leftSection={<UploadSimpleIcon size={20} className={styles.primaryIcon} />}
                    primary="Upload existing spreadsheet"
                    secondary="Upload your spreadsheet file and start working on it"
                    onClick={createWorksheetFromUpload}
                />
            </ScoopMenu>
        )
    }

    const handleNewLiveWorksheet = async (e) => {
        const { workspaceName } = metadata || {}
        if (workspaceName?.includes('Tour') && accountID !== 'A1') {
            await tourAlertModal.show({ item: 'live worksheet' })
        } else {
            setAnchorEl(e.currentTarget)
        }
    }

    const handleGoToDataset = () => {
        navigate(ROUTES.SOURCES, { state: { openNewDataset: true } })
    }

    const headerActions = !isLoading && !isMetadataLoading && metadata?.inboxes?.length > 0 && (
        <ScoopButton
            variant="default"
            size="sm"
            onClick={handleNewLiveWorksheet}
            className={styles.newWorksheet}
            leftSection={<PlusIcon size={16} weight="bold" />}
        >
            New Live Worksheet
        </ScoopButton>
    )

    return (
        <ScoopScreenLayout
            title={
                <>
                    Live Worksheets
                    <HelpBotPromptButton
                        prompt={HELP_BOT_PROMPTS.LIVE_WORKSHEETS_SCREEN}
                        variant="icon"
                        iconMinimal
                        tooltip="Sync spreadsheets for real-time analysis and updates. Click to learn more."
                        tooltipPlacement="right"
                    />
                </>
            }
            icon={
                <MicrosoftExcelLogoIcon size={24} weight="duotone" className={styles.iconPrimary} />
            }
            withSearchBar={!isLoading && !isMetadataLoading && metadata?.inboxes?.length > 0}
            searchPlaceholder="Search Worksheets"
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchWidth="400px"
            headerActions={headerActions}
            className="worksheet-screen-container"
        >
            <div className={styles.gridContainer}>
                {!isLoading && !isMetadataLoading && !metadata?.inboxes?.length ? (
                    <ScoopCenter className={styles.emptyStateContainer}>
                        <EmptyDataset
                            title={'Welcome to Live Worksheets'}
                            description={
                                'Effortlessly connect spreadsheets to visualize, analyze, and update data in real time.'
                            }
                            iconComponent={
                                <div className={styles.emptyIconWrapper}>
                                    <TableIcon
                                        size={32}
                                        weight="duotone"
                                        className={styles.iconDark}
                                    />
                                </div>
                            }
                            onClick={handleGoToDataset}
                        />
                    </ScoopCenter>
                ) : (
                    <ScoopTable
                        columns={colDefs}
                        rows={filteredData || rowData}
                        rowHeight={72}
                        headerHeight={44}
                        loading={isLoading || isMetadataLoading}
                        emptyMessage="No worksheets found"
                    />
                )}
            </div>
            {showUploadWorksheet && (
                <UploadWorksheet
                    isOpen={showUploadWorksheet}
                    onClose={closeUploadWorksheet}
                    onRefresh={handleRefresh}
                />
            )}
            {renderOptionDialog()}
        </ScoopScreenLayout>
    )
}
